// web/src/components/PriceStrip.tsx
import { useEffect, useState, useMemo } from "react";
import {
  readSpellgatherFloor,
  readVendorPrices,
  formatUsd,
} from "@/data/prices";
import type { PriceSnapshot } from "@/types/cards";

type Props = {
  scryfallId: string;
  fallbackUsd?: number; // optional Scryfall fallback for regular
  compact?: boolean; // tighter layout for grid tiles
};

export default function PriceStrip({
  scryfallId,
  fallbackUsd,
  compact,
}: Props) {
  const [vendors, setVendors] = useState<PriceSnapshot | null>(null);
  const [sg, setSg] = useState<number | null>(null);

  useEffect(() => {
    let aborted = false;
    setVendors(null);
    setSg(null);

    (async () => {
      const [vRes, sgRes] = await Promise.allSettled([
        readVendorPrices(scryfallId),
        readSpellgatherFloor(scryfallId),
      ]);
      if (aborted) return;

      if (vRes.status === "fulfilled") setVendors(vRes.value ?? null);
      else console.error("readVendorPrices failed:", vRes.reason);

      if (sgRes.status === "fulfilled" && typeof sgRes.value === "number") {
        setSg(sgRes.value);
      } else if (sgRes.status === "rejected") {
        console.error("readSpellgatherFloor failed:", sgRes.reason);
      }
    })();

    return () => {
      aborted = true;
    };
  }, [scryfallId]);

  const tcgRegular = useMemo(() => {
    return (
      vendors?.tcgplayer?.mid ??
      vendors?.tcgplayer?.market ??
      vendors?.tcgplayer?.low ??
      fallbackUsd ??
      null
    );
  }, [vendors, fallbackUsd]);

  // Foil comes from Scryfall usd_foil, which prices.ts maps to tcgplayer.high if no vendor foil is present
  const tcgFoil = useMemo(() => {
    return vendors?.tcgplayer?.high ?? null;
  }, [vendors]);

  const clsRow = compact
    ? "text-[15px] text-goldleading-4"
    : "text-md text-white/80";
  const clsLabel = "opacity-70";
  const clsVal = "font-semibold";

  return (
    <div className={`grid ${compact ? "gap-0.5" : "gap-1"} mt-1`}>
      <div className={`${clsRow} flex justify-between`}>
        <span className={clsLabel}>TCGplayer</span>
        <span className={clsVal}>{formatUsd(tcgRegular)}</span>
      </div>
      <div className={`${clsRow} flex justify-between`}>
        <span className={clsLabel}>TCGplayer (Foil)</span>
        <span className={clsVal}>{formatUsd(tcgFoil)}</span>
      </div>

      {/*
      <div className={`${clsRow} flex justify-between`}>
        <span className={clsLabel}>Card Kingdom</span>
        <span className={clsVal}>{formatUsd(vendors?.cardkingdom?.retail)}</span>
      </div>
      <div className={`${clsRow} flex justify-between`}>
        <span className={clsLabel}>Star City</span>
        <span className={clsVal}>{formatUsd(vendors?.starcity?.retail)}</span>
      </div>
      */}

      <div className={`${clsRow} flex justify-between`}>
        <span className={clsLabel}>Spellgather</span>
        <span className={clsVal}>{formatUsd(sg)}</span>
      </div>
    </div>
  );
}

